/**
 * One colour per car, assigned by position in the list. Every surface that
 * refers to a ride (map line, origin pin, stop pins, card border, seat strip)
 * uses the same colour, so the sidebar and the map read as one object.
 * Hues are picked to stay distinguishable on light OSM raster tiles.
 */
export const RIDE_COLORS = [
  '#4f8cff', '#28c6a0', '#c77dff', '#ffb020', '#ff5d8f',
  '#5ee1a0', '#7c8cff', '#ff8a4c', '#2dd4bf', '#e879f9',
];

export const rideColor = (index) => RIDE_COLORS[index % RIDE_COLORS.length];

/** Stable index map so colours do not shuffle when a car is added or removed. */
export function colorMap(rides) {
  const map = new Map();
  rides.forEach((ride, i) => map.set(ride.id, rideColor(i)));
  return map;
}


/* ------------------------------------------------------------- leg shades -- */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function hexToHsl(hex) {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Shades of one car's colour, one per leg between official stops.
 *
 * A route with a pickup in the middle reads as one undifferentiated line, and it
 * is genuinely hard to tell which half comes first. Varying lightness along the
 * journey, hue untouched, keeps every leg recognisably the same car while making
 * the break at each stop visible. Early legs are lighter and the final approach
 * is darkest, so the line reads in the direction of travel.
 *
 * Via points never produce a shade: they shape the road, they are not a stage of
 * the journey, and treating them as one would invent stops that do not exist.
 */
export function legShades(baseColor, count) {
  if (count <= 1) return [baseColor];
  const { h, s, l } = hexToHsl(baseColor);
  // Wide enough to be read at a glance on a busy basemap, narrow enough that the
  // legs still look like one car. Too subtle and the split is invisible, which is
  // the whole point of drawing it.
  const span = Math.min(40, 20 + count * 6);
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1); // 0 at the start, 1 on the final approach
    const lightness = clamp(l + span / 2 - span * t, 34, 72);
    const saturation = clamp(s + t * 8, 0, 100);
    return `hsl(${h.toFixed(0)} ${saturation.toFixed(0)}% ${lightness.toFixed(0)}%)`;
  });
}
